/**
 * Analysis Results Page
 *
 * Dedicated page for viewing completed analysis results.
 * Redirects to the job progress page if the analysis is not yet complete.
 */

import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { ErrorState } from '../components/ErrorState';
import Footer from '../components/Footer';
import { Navbar } from '../components/Navbar';
import { AnalysisResultsView } from '../components/analysis/AnalysisResultsView';
import { Breadcrumbs, LoadingSpinner } from '../components/common';
import type { BreadcrumbItem } from '../components/common/Breadcrumbs';
import type { RepositoryAnalysis } from '../components/types/analysis.types';
import { useAnalysisJob } from '../hooks/useAnalysisJob';
import { analytics } from '../services/analytics/posthog';
import { Repository } from '../types';

export const AnalysisResultsPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Get repository and navigation context from state
  const repository = location.state?.repository as Repository | undefined;
  const fromPage = location.state?.from as string | undefined;

  const { jobStatus, loading, error, handleRetry } = useAnalysisJob(jobId);

  useEffect(() => {
    analytics.pageViewed('analysis_results');
  }, []);

  useEffect(() => {
    if (!loading && jobStatus?.status === 'completed' && jobStatus.result && jobId) {
      analytics.analysisViewed(jobId, 'full');
    }
  }, [loading, jobStatus?.status, jobStatus?.result, jobId]);

  // Redirect to progress page if job is not completed
  useEffect(() => {
    if (!loading && jobStatus) {
      const isInProgress = jobStatus.status === 'running' || jobStatus.status === 'pending';
      if (isInProgress) {
        navigate(`/jobs/${jobId}`, { replace: true, state: location.state });
      }
    }
  }, [loading, jobStatus, jobId, navigate, location.state]);

  // Repository identity from location state (preferred) with fallback to job status.
  const repoId = repository?.id ?? jobStatus?.repository?.id;
  const repoName = repository?.fullName ?? jobStatus?.repository?.fullName;

  // Navigation handlers
  const handleBackToRepository = () => {
    if (repoId) {
      navigate(`/repository/${repoId}`, {
        state: repository ? { repository, from: fromPage } : undefined,
      });
    } else {
      navigate('/repositories');
    }
  };

  const errorBreadcrumbs: BreadcrumbItem[] = [
    { label: 'All Scans', path: '/history' },
    ...(repoName && repoId ? [{ label: repoName, path: `/repository/${repoId}` }] : []),
    { label: 'Analysis' },
  ];

  const onRetry = async () => {
    const newJobId = await handleRetry();
    if (newJobId) {
      navigate(`/jobs/${newJobId}`, { replace: true });
    }
  };

  // The job couldn't be loaded, so there's nothing to retry by id — retrying
  // would re-hit the same dead job and loop. Send the user into the setup flow.
  const startNewAnalysis = () => {
    if (repoId) {
      navigate(`/repository/${repoId}`, {
        state: repository ? { repository, from: fromPage } : undefined,
      });
    } else {
      navigate('/repositories');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <Navbar pageTitle="Repository Findings" showDivider={true} />
        <div className="flex items-center justify-center pt-32">
          <div className="text-center">
            <LoadingSpinner size="lg" className="mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading analysis results...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state or job not found
  if (error || !jobStatus) {
    return (
      <div className="min-h-screen flex flex-col bg-white dark:bg-gray-900">
        <Navbar pageTitle="Analysis Results" showDivider={true} />
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-8 pt-24">
          <div className="max-w-7xl mx-auto w-full">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-8 border border-gray-200 dark:border-gray-700">
              <ErrorState
                title="Unable to Load Results"
                message={
                  error ||
                  "We couldn't find this analysis. It may have been removed or the link is incorrect."
                }
                onRetry={startNewAnalysis}
                retryText="Start New Analysis"
                type="generic"
              />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Job failed or cancelled - show appropriate message
  if (jobStatus.status === 'failed' || jobStatus.status === 'cancelled') {
    return (
      <div className="min-h-screen flex flex-col bg-white dark:bg-gray-900">
        <Navbar pageTitle={repoName || 'Analysis Results'} showDivider={true} />
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-8 pt-24">
          <div className="max-w-7xl mx-auto w-full">
            <Breadcrumbs items={errorBreadcrumbs} />
            <div className="bg-white dark:bg-gray-800 rounded-lg p-8 border border-gray-200 dark:border-gray-700">
              <ErrorState
                title={jobStatus.status === 'failed' ? 'Analysis Failed' : 'Analysis Cancelled'}
                message={
                  jobStatus.status === 'failed'
                    ? jobStatus.error || 'The analysis encountered an error. Please try again.'
                    : 'The analysis was cancelled before it could complete.'
                }
                onRetry={onRetry}
                retryText="Start New Analysis"
                type="generic"
              />
              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
                <button
                  onClick={handleBackToRepository}
                  className="inline-flex items-center gap-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {repoId ? 'Back to repository' : 'Back to repositories'}
                </button>
                <Link
                  to="/history"
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  View scan history
                </Link>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Job completed but no results
  if (jobStatus.status === 'completed' && !jobStatus.result) {
    return (
      <div className="min-h-screen flex flex-col bg-white dark:bg-gray-900">
        <Navbar pageTitle="Analysis Results" showDivider={true} />
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-8 pt-24">
          <div className="max-w-7xl mx-auto w-full">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-8 border border-gray-200 dark:border-gray-700">
              <ErrorState
                title="Results Not Available"
                message="The analysis completed but results are not available. Please try running a new analysis."
                onRetry={onRetry}
                retryText="Start New Analysis"
                type="generic"
              />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Job completed with results - render the results view
  if (!jobStatus.result) return null;
  // API boundary: result is unstructured JSON validated by the backend
  const analysisData = jobStatus.result as unknown as RepositoryAnalysis;

  return (
    <div className="min-h-screen flex flex-col transition-colors duration-200 bg-white dark:bg-gray-900">
      <Navbar pageTitle="Repository Findings" showDivider={true} />
      <main className="flex-1 w-full pt-24">
        <AnalysisResultsView data={analysisData} onBackToRepository={handleBackToRepository} />
      </main>
      <Footer />
    </div>
  );
};
